/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React, { useEffect, useState, useRef } from 'react';
import { NodeProps, Position, Handle } from 'reactflow';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import NodeHeader from 'src/components/ui/NodeHeader/NodeHeader';
import Select, { Option } from 'src/components/ui/Select/Select';
import Input from 'src/components/ui/Input/Input';
import { useOutsideClick } from 'src/hooks/utils';
import { setNodeConfiguration } from 'src/redux/workflows/slice';
import { Insert, useInsertProducts } from 'src/services/productsNew';
import Icon from 'src/components/ui/Icon/Icon';

const ProductBuilderNode: React.FC<NodeProps> = ({ id, data }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [selectedProduct, setSelectedProduct] = useState<Insert | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<Option | null>(null);
  const [amountValue, setAmountValue] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const [isProductMenuOpen, setIsProductMenuOpen] = useState(false);
  const [isUnitMenuOpen, setIsUnitMenuOpen] = useState(false);

  const productSelectRef = useRef<HTMLDivElement>(null);
  const productInputRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { items: insertProductsData } = useInsertProducts();

  const unitOptions: Option[] = [
    {
      label: t('workflows.per_order'),
      value: 'order',
    },
    {
      label: t('workflows.per_product'),
      value: 'product',
    },
  ];

  const filteredProducts =
    insertProductsData?.filter(
      product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) || product.id.toString().includes(searchQuery),
    ) || [];

  useEffect(() => {
    if (data.actionData) {
      const { selectedProductId, unit, amount } = data.actionData;

      if (selectedProductId && insertProductsData) {
        const product = insertProductsData.find(p => p.id === selectedProductId);
        if (product) {
          setSelectedProduct(product);
        }
      }

      if (unit) {
        const unitOption = unitOptions.find(option => option.value === unit);
        if (unitOption) {
          setSelectedUnit(unitOption);
        }
      }

      if (amount) {
        setAmountValue(amount.toString());
      }
    }
  }, [data.actionData, insertProductsData]);

  useOutsideClick(menuRef, e => {
    if (!productInputRef.current?.contains(e.target as Node)) {
      setIsProductMenuOpen(false);
    }
  });

  useEffect(() => {
    if (isProductMenuOpen) {
      const handleWheel = (e: WheelEvent) => {
        const target = e.target as Element;
        if (menuRef.current?.contains(target)) {
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      };

      document.addEventListener('wheel', handleWheel, { passive: false, capture: true });

      return () => {
        document.removeEventListener('wheel', handleWheel, { capture: true });
      };
    }
    return undefined;
  }, [isProductMenuOpen]);

  const handleResetClick = () => {
    if (data.isEditMode && data.removeNode) {
      data.removeNode(id);
    } else if (data.isEditMode && data.removeLastNode) {
      data.removeLastNode();
      data.removeLastNode();
    }
  };

  const selectProduct = (productId: number | null | undefined) => {
    if (!productId) return;

    const product = insertProductsData?.find(product => product.id === productId);
    if (product) {
      setSelectedProduct(product);
      setIsProductMenuOpen(false);
    }
  };

  const handleProductClick = () => {
    if (data.isEditMode) {
      setIsProductMenuOpen(!isProductMenuOpen);
      setIsUnitMenuOpen(false);
    }
  };

  const handleUnitClick = () => {
    if (data.isEditMode) {
      setIsUnitMenuOpen(!isUnitMenuOpen);
      setIsProductMenuOpen(false);
    }
  };

  const handleUnitChange = (option: Option) => {
    setSelectedUnit(option);
    setIsUnitMenuOpen(false);
  };

  const getProductDisplayValue = () => {
    return selectedProduct?.name || null;
  };

  const actionTitle = data.actionData?.title || 'Produkt beilegen';

  useEffect(() => {
    const selectedProductId = selectedProduct?.id || undefined;
    const unit = selectedUnit?.value || undefined;
    const amount = amountValue ? parseInt(amountValue, 10) : undefined;

    dispatch(
      setNodeConfiguration({
        nodeId: id,
        nodeType: 'action',
        data: {
          actionType: 'product',
          title: actionTitle,
          returnValue: selectedProductId ? selectedProductId.toString() : '',
          selectedProductId,
          unit,
          amount,
        },
      }),
    );
  }, [dispatch, id, actionTitle, selectedProduct, selectedUnit, amountValue]);

  return (
    <div className="surface-elevated rounded-base border border-subtle relative overflow-visible !w-[48rem]">
      <div className="w-full">
        <div className="py-16 px-20">
          <NodeHeader
            title={actionTitle}
            subtitle="Aktion"
            icon="send"
            backgroundColor="var(--primary)"
            onActionClick={handleResetClick}
            isEditMode={data.isEditMode}
          />
        </div>
        <div className="px-20 pb-20 space-y-8">
          <div className="relative">
            <p className="typo-text-14--bold text-faded mb-[12px]">{t('workflows.select_product')}</p>
            <div
              ref={productSelectRef}
              onClick={handleProductClick}
              role="none"
              onMouseDown={event => event.stopPropagation()}>
              <div
                ref={productInputRef}
                className={classNames(
                  'px-12 py-5 rounded-base border-distinct cursor-pointer min-h-[36px] flex items-center justify-between',
                  { 'border-focus-ring': isProductMenuOpen },
                  { 'surface-disabled': !data.isEditMode },
                )}>
                <span
                  className={classNames('typo-text-14', {
                    'text-faded': !getProductDisplayValue() || !data.isEditMode,
                    'text-full': getProductDisplayValue(),
                  })}>
                  {getProductDisplayValue() || t('general.select')}
                </span>
                <Icon name="accordion-arrow-down" width={22} height={22} className="opacity-50" />
              </div>
            </div>

            {isProductMenuOpen && data.isEditMode && (
              <div
                ref={menuRef}
                className="absolute top-full left-0 right-0 z-50 surface-elevated p-12 space-y-6 shadow-lg rounded-base border-subtle mt-2"
                onMouseDown={event => event.stopPropagation()}
                onMouseUp={event => event.stopPropagation()}
                onMouseMove={event => event.stopPropagation()}
                onTouchStart={event => event.stopPropagation()}
                onTouchMove={event => event.stopPropagation()}
                onTouchEnd={event => event.stopPropagation()}>
                <div onClick={event => event.stopPropagation()}>
                  <Input
                    value={searchQuery}
                    onChange={event => setSearchQuery(event.target.value)}
                    placeholder={t('general.search_placeholder')}
                    className="w-full"
                    icon="search"
                  />
                </div>

                <div
                  ref={scrollContainerRef}
                  className="border border-distinct rounded-base max-h-[300px] overflow-y-auto"
                  style={{
                    overscrollBehavior: 'contain',
                  }}
                  onWheel={event => {
                    event.preventDefault();
                    event.stopPropagation();

                    const container = event.currentTarget;
                    const scrollAmount = event.deltaY;

                    container.scrollTop += scrollAmount;
                  }}
                  onScroll={event => {
                    event.stopPropagation();
                  }}
                  onMouseDown={event => event.stopPropagation()}
                  onMouseUp={event => event.stopPropagation()}
                  onMouseMove={event => event.stopPropagation()}>
                  {filteredProducts.length === 0 && (
                    <div className="p-12 typo-text-12 text-faded text-center">{t('general.no_products_found')}</div>
                  )}
                  {filteredProducts.map(product => {
                    const isSelected = selectedProduct?.id === product.id;
                    return (
                      <div
                        key={product.id}
                        role="none"
                        onClick={event => {
                          event.stopPropagation();
                          selectProduct(product?.id);
                        }}
                        onMouseDown={event => event.stopPropagation()}
                        onMouseUp={event => event.stopPropagation()}
                        className={classNames(
                          'flex items-center gap-12 p-12 cursor-pointer transition-colors hover:surface-elevated-more',
                          { 'bg-blue-50 border-l-4 border-primary': isSelected },
                        )}>
                        <div className="flex flex-col gap-2 flex-1">
                          <span className="typo-text-14 text-full">{product.name}</span>
                          <span className="typo-text-12 text-faded">ID: {product.id}</span>
                        </div>
                        {isSelected && <Icon name="checkmark" width={16} height={16} className="text-primary" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div>
            <p className="typo-text-14--bold text-faded mb-[12px]">{t('workflows.select_unit')}</p>
            <div
              onClick={handleUnitClick}
              role="none"
              onMouseDown={event => event.stopPropagation()}
              style={{ position: 'relative', zIndex: isUnitMenuOpen ? 1000 : 1 }}>
              <Select
                options={unitOptions}
                placeholder={t('general.select')}
                onChange={handleUnitChange}
                defaultValue={selectedUnit || undefined}
                menuPlacement="auto"
                isMenuOpen={data.isEditMode ? isUnitMenuOpen : false}
                disabled={!data.isEditMode}
              />
            </div>
          </div>

          <div className="flex flex-col items-start">
            <p className="typo-text-14--bold text-faded mb-[12px]">{t('workflows.amount_per_unit')}</p>
            <Input
              value={amountValue}
              onChange={event => setAmountValue(event.target.value)}
              placeholder={t('workflows.enter_amount')}
              disabled={!data.isEditMode}
              error={amountValue !== '' && (Number.isNaN(Number(amountValue)) || Number(amountValue) < 1)}
              errorMessage={t('general.invalid_quantity')}
            />
          </div>
        </div>
        <Handle
          type="source"
          position={Position.Right}
          id={`${id}`}
          style={{
            opacity: 0,
            top: '37px',
            position: 'absolute',
            right: '0%',
            width: '1px !important',
            height: '1px !important',
          }}
        />
        <Handle
          type="source"
          position={Position.Left}
          id={`${id}`}
          style={{
            opacity: 0,
            top: '37px',
            position: 'absolute',
            left: '0%',
            width: '1px !important',
            height: '1px !important',
          }}
        />
        <Handle
          type="source"
          position={Position.Top}
          id={`${id}`}
          style={{
            opacity: 0,
            top: '37px',
          }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id={`${id}`}
          style={{
            opacity: 0,
            top: '37px',
          }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id={`${id}`}
          style={{
            opacity: 0,
            top: '37px',
            position: 'absolute',
            left: '0%',
            width: '1px !important',
            height: '1px !important',
          }}
        />
      </div>
    </div>
  );
};

export default ProductBuilderNode;
